import React, { useEffect, useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  Pagination,
  FunctionField,
  useDataProvider,
  useRefresh,
  useGetIdentity,
  useNotify,
  usePermissions,
} from 'react-admin';
import BillingClientsFtpsFilters from './BillingClientsFtpsFilters';
import { formatTimestamp } from '../utils/DateUtils';
import FtpsEditDialog from './FtpsEditDialog';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DEFAULTS } from '../constants';

const BillingClientsFtps = () => {
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const { permissions } = usePermissions();

  const [recordToDelete, setRecordToDelete] = useState<any | null>(null);

  const [editId, setEditId] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formValues, setFormValues] = useState<any>(null);
  const [originalValues, setOriginalValues] = useState<any>(null);

  const handleEdit = (record: any) => {
    setEditId(record.id);
    setFormValues({ ...record });
    setOriginalValues({ ...record });
    setIsDialogOpen(true);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setEditId(null);
    setFormValues(null);
  };

  const handleSave = async () => {
    const changedFields: Record<string, any> = {};

    Object.keys(formValues).forEach((key) => {
      if (formValues[key] !== originalValues[key]) {
        changedFields[key] = formValues[key];
      }
    });

    if (Object.keys(changedFields).length === 0) {
      notify('No changes detected', { type: 'info' });
      handleClose();
      return;
    }

    try {
      await dataProvider.update('BillingClientsFtps', {
        id: editId,
        data: {
          ...changedFields,
          last_modified_by_text: identity?.fullName || 'Unknown User',
        },
        previousData: originalValues,
      });
      notify('notifications.CLIENT_FTP.UPDATE_SUCCESS', {
        type: 'success',
      });
      refresh();
      handleClose();
    } catch (error) {
      notify('notifications.CLIENT_FTP.UPDATE_FAILED', {
        type: 'error',
      });
    }
  };

  const handleDeleteClick = (record: any) => {
    setRecordToDelete(record);
  };

  const handleDeleteConfirm = async () => {
    if (!recordToDelete) return;

    try {
      await dataProvider.delete('BillingClientsFtps', {
        id: recordToDelete.id,
        previousData: {
          id: recordToDelete.id,
          user: identity?.fullName || DEFAULTS.UNKNOWN_USER,
        },
      });

      notify('notifications.CLIENT_FTP.DELETE_SUCCESS', { type: 'success' });
      refresh();
    } catch (error: any) {
      notify('notifications.CLIENT_FTP.DELETE_FAILED', { type: 'error' });
    }

    setRecordToDelete(null);
  };

  return (
    <>
      <List
        resource="BillingClientsFtps"
        perPage={10}
        pagination={<Pagination />}
        sort={{ field: 'billing_client_number', order: 'ASC' }}
        actions={false}
        filters={<BillingClientsFtpsFilters />}
        sx={{ '& .RaList-main': { p: 0 } }}
      >
        <Datagrid bulkActionButtons={false}>
          <TextField source="billing_client_number" label="Billing Client #" />
          <TextField source="billing_client_name" label="Billing Client Name" />
          <TextField source="partial_client_name" label="Partial Client Name" />
          <TextField source="ftp_folder_name" label="FTP Folder" />
          <TextField source="created_by_text" label="Created By" />
          <TextField source="last_modified_by_text" label="Last Modified By" />
          <FunctionField
            label="Last Modified Date"
            render={(record: any) => formatTimestamp(record.last_modified_ts)}
          />
          {permissions.adminPermission && (
            <FunctionField
              label="Actions"
              render={(record: any) => (
                <Box display="flex" gap={1}>
                  <IconButton onClick={() => handleEdit(record)} sx={{ color: '#00909A' }}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteClick(record)} sx={{ color: '#00909A' }}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}
            />
          )}
        </Datagrid>
      </List>

      {isDialogOpen && formValues && (
        <FtpsEditDialog
          open={isDialogOpen}
          formValues={formValues}
          setFormValues={setFormValues}
          originalValues={originalValues}
          onClose={handleClose}
          onSave={handleSave}
        />
      )}
      <Dialog open={!!recordToDelete} onClose={() => setRecordToDelete(null)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the FTP configuration for{' '}
            <strong>{recordToDelete?.billing_client_name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordToDelete(null)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="primary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BillingClientsFtps;
